/**
 * AuthContext — JWT-based session against auth_server.py (port 8002).
 *
 * Session shape: { user_id, username, role, token }
 * Persistence:   localStorage key "dashboard.session.v1"
 * Token:         JWT, sent as `Authorization: Bearer <token>` on every API call
 * Helper:        apiFetch() injects the bearer token automatically
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export type Role = 'admin' | 'user';

export interface Session {
  user_id: string;
  username: string;
  role: Role;
  token: string;
}

interface AuthContextValue {
  session: Session | null;
  login: (
    username: string,
    password: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  register: (
    username: string,
    password: string,
  ) => Promise<
    | { ok: true; pending: boolean; message: string }
    | { ok: false; error: string }
  >;
  logout: () => void;
}

const STORAGE_KEY = 'dashboard.session.v1';

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (
      parsed &&
      typeof parsed.user_id === 'string' &&
      typeof parsed.username === 'string' &&
      (parsed.role === 'admin' || parsed.role === 'user') &&
      typeof parsed.token === 'string'
    ) {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * apiFetch — wraps fetch() with the JWT bearer token from stored session.
 * Use this for all calls to /auth, /parquet, /preset, /activity.
 */
export async function apiFetch(
  input: RequestInfo,
  init: RequestInit = {},
): Promise<Response> {
  const session = readStoredSession();
  const headers = new Headers(init.headers || {});
  if (session?.token) headers.set('Authorization', `Bearer ${session.token}`);
  if (init.body && !headers.has('Content-Type'))
    headers.set('Content-Type', 'application/json');
  const res = await fetch(input, { ...init, headers });
  // If our token is no longer accepted, kill the session so background
  // polls stop hammering the server with 401s.
  if (res.status === 401 && session) {
    localStorage.removeItem(STORAGE_KEY);
    // Skip reload if we're already on the login page (prevents loops).
    if (!/\/(login|register)?$/.test(window.location.pathname)) {
      window.location.href = '/';
    }
  }
  return res;
}

/**
 * One-time migration of localStorage RowTabs and Presets into ClickHouse.
 * Runs after first successful login. Idempotent — keys are deleted after upload.
 */
async function maybeMigrateLocalStorage(token: string): Promise<void> {
  const auth = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  // Parquet RowTabs
  try {
    const rawRowTabs = localStorage.getItem('parquet_row_tabs');
    if (rawRowTabs) {
      const tabs = JSON.parse(rawRowTabs);
      if (Array.isArray(tabs) && tabs.length > 0) {
        const payload = tabs.map((t: any) => ({
          name: t.name || 'untitled',
          coins: t.coins || [],
          timeframes: t.timeframes || [],
          data_types: t.dataTypes || t.data_types || [],
          data_source: t.dataSource || t.data_source || 'cleaned',
          date_from: t.dateFrom || t.date_from || '',
          date_to: t.dateTo || t.date_to || '',
        }));
        await fetch('/parquet/migrate', {
          method: 'POST',
          headers: auth,
          body: JSON.stringify(payload),
        });
        localStorage.removeItem('parquet_row_tabs');
      }
    }
  } catch {
    /* ignore */
  }

  // Presets
  try {
    const rawPresets = localStorage.getItem('parquet_presets');
    if (rawPresets) {
      const presets = JSON.parse(rawPresets);
      if (Array.isArray(presets) && presets.length > 0) {
        const payload = presets.map((p: any) => ({
          name: p.name || 'untitled',
          coins: p.coins || [],
          timeframes: p.timeframes || [],
          data_types: p.dataTypes || p.data_types || [],
          data_source: p.dataSource || p.data_source || 'cleaned',
          date_from: p.dateFrom || p.date_from || '',
          date_to: p.dateTo || p.date_to || '',
        }));
        await fetch('/preset/migrate', {
          method: 'POST',
          headers: auth,
          body: JSON.stringify(payload),
        });
        localStorage.removeItem('parquet_presets');
      }
    }
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(() =>
    readStoredSession(),
  );

  useEffect(() => {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  }, [session]);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const r = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        return { ok: false as const, error: j.detail || 'Login failed' };
      }
      const data = await r.json();
      const newSession: Session = {
        user_id: data.user_id,
        username: data.username,
        role: data.role,
        token: data.token,
      };
      // Persist synchronously BEFORE setSession so that apiFetch() can
      // read the token from localStorage immediately on the next render.
      // (The useEffect that also persists will be a harmless no-op.)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
      setSession(newSession);
      // Run one-time migration in the background
      maybeMigrateLocalStorage(newSession.token).catch(() => {});
      return { ok: true as const };
    } catch (err) {
      return {
        ok: false as const,
        error: 'Network error — is the auth server running?',
      };
    }
  }, []);

  const register = useCallback(async (username: string, password: string) => {
    try {
      const r = await fetch('/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        return { ok: false as const, error: j.detail || 'Registration failed' };
      }
      const data = await r.json();
      // New accounts are created UNAPPROVED — no token is issued. The user must
      // wait for an admin to approve the account before they can sign in.
      return {
        ok: true as const,
        pending: true,
        message:
          data.message ||
          'Account created. An admin must approve it before you can sign in.',
      };
    } catch (err) {
      return {
        ok: false as const,
        error: 'Network error — is the auth server running?',
      };
    }
  }, []);

  const logout = useCallback(() => {
    // Best-effort server notification
    apiFetch('/auth/logout', { method: 'POST' }).catch(() => {});
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider value={{ session, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
